import type { AdornedInputWrapperProps } from '../input/InputWithAdornments';
import { faMinus } from '@fortawesome/pro-regular-svg-icons/faMinus';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@thinkalpha/ui-core/utils';
import { noop } from 'es-toolkit';
import { useCallback } from 'react';
import { AdornedInputPrimitive } from 'src/components/ui';
import { useEvent } from 'src/hooks/useEvent';

export const StepperButton = ({
    className,
    disabled,
    ...rest
}: {
    onClick: () => void;
    disabled?: boolean;
    children: React.ReactNode;
    className?: string;
}) => {
    return (
        <button
            type="button"
            disabled={disabled}
            className={cn(
                'h-8 px-1.5',
                disabled ? 'bg-gray-700' : 'bg-gray-100 font-bold text-slate-500 hover:bg-gray-200 hover:text-gray-700',
                className,
            )}
            tabIndex={-1}
            {...rest}
        />
    );
};

export type StepperInputProps = {
    value: number | string;
    onChange: (v: number | string) => void;
    disabled?: boolean;
    dimensions?: AdornedInputWrapperProps['dimensions'];
    variant?: AdornedInputWrapperProps['variant'];

    /**
     * Value used for increment/decrement actions.
     * @default 0.01
     */
    step?: number;

    /**
     * Mantissa/significant for the number in the input
     * @default 2
     */
    precision?: number;
    className?: string;
    id?: string;
};

export const StepperInput = ({
    precision = 2,
    step = 0.01,
    value,
    onChange = noop,
    disabled,
    className,
    dimensions = 'sm',
    variant = 'default',
    id = undefined,
}: StepperInputProps) => {
    // stringer helper
    const toPreciseString = useCallback((v: number) => v.toFixed(precision), [precision]);
    const processChange = useEvent((value: string) => {
        onChange(value);
    });
    const handleChange = useEvent((e: React.ChangeEvent<HTMLInputElement>) => {
        processChange(e.currentTarget.value);
    });
    const handleInputKeyDown = useEvent((e: React.KeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                handleDecrement();
                break;
            case 'ArrowUp':
                e.preventDefault();
                handleIncrement();
                break;
            default:
                break;
        }
    });
    const handleDecrement = useEvent(() => {
        const newValue = Math.max(0, Number(value) - step);
        if (newValue < step) {
            processChange(toPreciseString(step));
            return;
        }
        processChange(toPreciseString(newValue));
    });
    const handleIncrement = useEvent(() => {
        const newValue = Number(value) + step;
        processChange(toPreciseString(newValue));
    });

    return (
        <AdornedInputPrimitive.Wrapper
            className={cn('overflow-clip', className)}
            dimensions={dimensions}
            variant={variant}
            disabled={disabled}
            preAdornment
            postAdornment
        >
            <AdornedInputPrimitive.Adornment className="px-0">
                <StepperButton onClick={handleDecrement} disabled={disabled} data-testid="dec-button">
                    <FontAwesomeIcon icon={faMinus} />
                </StepperButton>
            </AdornedInputPrimitive.Adornment>
            <AdornedInputPrimitive.Input
                id={id}
                disabled={disabled}
                className="pl-1"
                type="number"
                value={value}
                onChange={handleChange}
                // @ts-expect-error - fieldSizing is not recognized as a valid css style, but it is for our purposes
                style={{ fieldSizing: 'content' }}
                onKeyDown={handleInputKeyDown}
                data-testid="input"
            />
            <AdornedInputPrimitive.Adornment className="px-0">
                <StepperButton onClick={handleIncrement} disabled={disabled} data-testid="inc-button">
                    <FontAwesomeIcon icon={faPlus} />
                </StepperButton>
            </AdornedInputPrimitive.Adornment>
        </AdornedInputPrimitive.Wrapper>
    );
};
